import { CREATOR_ACCOUNT_TYPE } from "./constants";
import { store } from "../redux/redux";
import { ftuxAPI } from "../services/ftuxService";

// Helper function to check if a feature is in FTUX takeover
export const isFeatureInFTUXTakeover = (feature) => {
  try {
    const state = store.getState();
    const ftuxTakeoverData =
      ftuxAPI.endpoints.getFTUXHomeTakeoverFlow.select(undefined)(state);
    if (
      ftuxTakeoverData?.data?.success &&
      Array.isArray(ftuxTakeoverData.data.data)
    ) {
      return ftuxTakeoverData.data.data.some(
        (item) => item.feature === feature || item.feature_name === feature
      );
    }
    return false;
  } catch (error) {
    return false;
  }
};

// Helper function to check if FTUX has been completed (REALISATION_COMPLETED)
export const isFTUXCompleted = (feature) => {
  try {
    const state = store.getState();
    const ftuxTakeoverData =
      ftuxAPI.endpoints.getFTUXHomeTakeoverFlow.select(undefined)(state);

    if (
      ftuxTakeoverData?.data?.success &&
      Array.isArray(ftuxTakeoverData.data.data)
    ) {
      const featureData = ftuxTakeoverData.data.data.find(
        (item) => item.feature === feature || item.feature_name === feature
      );
      // If feature is not in the takeover data anymore, it means it was completed and removed
      return !featureData;
    }
    return false;
  } catch (error) {
    return false;
  }
};

// User Account Type Detection based on requirements
export const getUserAccountType = (creatorData, milestones) => {
  const accountType = creatorData?.creator_account_type;

  // Telegram only accounts
  if (accountType === CREATOR_ACCOUNT_TYPE.AFFILIATE) {
    return "TELEGRAM";
  }

  // Social Login accounts
  if (accountType === CREATOR_ACCOUNT_TYPE.SOCIAL) {
    const hasInstagram = creatorData.is_connected_insta;
    const hasFacebook = creatorData.is_connected_facebook;
    const hasYoutube = creatorData.is_connected_yt;

    // Youtube only accounts
    if (hasYoutube && !hasInstagram && !hasFacebook) {
      return "YOUTUBE_ONLY_SL";
    }

    if (hasInstagram && hasFacebook) {
      return "INSTAGRAM_AND_FACEBOOK_SL";
    }

    // Instagram/Facebook SL accounts (with or without YouTube)
    if (hasInstagram || hasFacebook) {
      return "INSTAGRAM_OR_FACEBOOK_SL";
    }
  }

  return "NEW_USER_SL"; // Default fallback
};

// Check if Engage is linked (Instagram or Facebook)
export const checkEngageLinked = (creatorData) => {
  return !!(creatorData?.is_connected_ctk || creatorData?.ENGAGE_CONNECTED);
};
// Check FTUX realisation seen status
export const checkFTUXRealisationSeen = (feature, milestones) => {
  const realisationKey = `${feature}_REALISATION_SEEN`;
  return milestones?.[realisationKey]?.achieved || false;
};

// Helper functions for checking various states
export const checkHasGrowthRecommendation = (milestones) => {
  return milestones?.GROWTH_V2__PRODUCT_GROWTH?.achieved || false;
};

export const checkHasVisibleCollab = (creatorData) => {
  return creatorData?.BRAND_COLLAB__COLLAB_LIVE || false;
};

// Get Brand Collab access state based on requirements
export const getCollabAccessState = (
  milestones,
  creatorData,
  collabFormFilled = false
) => {
  const collabLive = milestones?.BRAND_COLLAB__COLLAB_LIVE?.achieved;
  // check if collab access granted and collab ftux is completed from home page takeover from userFTUX NOT FROM MILESTONE
  const collabFTUXCompleted = !isFeatureInFTUXTakeover("BRAND_COLLAB");
  // If user has live access, always show as direct access (let route guards handle FTUX)
  if (collabLive && collabFTUXCompleted) {
    return { status: "NO_TAG", showFTUX: false, type: "DIRECT_ACCESS" };
  } else {
    return {
      status: collabLive
        ? collabFTUXCompleted
          ? "NO_TAG"
          : "UPDATE"
        : "NO_TAG",
      showFTUX: true,
      type: "EDUCATION_WITH_REALISATION",
    };
  }
};

// Get Growth access state
export const getGrowthAccessState = (milestones) => {
  const hasRecommendation = checkHasGrowthRecommendation(milestones);
  // check if growth ftux is completed from home page takeover from userFTUX NOT FROM MILESTONE
  const growthFTUXCompleted = !isFeatureInFTUXTakeover("GROWTH_V2");
  if (hasRecommendation && growthFTUXCompleted) {
    return { status: "NO_TAG", showFTUX: false, type: "DIRECT_ACCESS" };
  } else {
    return {
      status: hasRecommendation
        ? growthFTUXCompleted
          ? "NO_TAG"
          : "UPDATE"
        : "NO_TAG",
      showFTUX: true,
      type: "EDUCATION_WITH_REALISATION",
    };
  }
};

// Get Rewards access state
export const getRewardsAccessState = (milestones) => {
  const hasRewardsAccess = milestones?.REWARDS__REWARDS_LIVE?.achieved;
  // check if rewards ftux is completed from home page takeover from userFTUX NOT FROM MILESTONE
  const rewardsFTUXCompleted = !isFeatureInFTUXTakeover("REWARDS");
  if (hasRewardsAccess && rewardsFTUXCompleted) {
    return { status: "NO_TAG", showFTUX: false, type: "DIRECT_ACCESS" };
  }
  if (!hasRewardsAccess) {
    return { status: "NO_TAG", showFTUX: true, type: "EDUCATION_ONLY" };
  }

  return {
    status: rewardsFTUXCompleted ? "NO_TAG" : "UPDATE",
    showFTUX: rewardsFTUXCompleted,
    type: "EDUCATION_WITH_REALISATION",
  };
};

// Get Streaks access state
export const getStreaksAccessState = (milestones) => {
  const hasStreaksAccess = milestones?.STREAKS_V2__enabled?.achieved;
  const streaksFTUXCompleted = !isFeatureInFTUXTakeover("STREAKS");
  if (hasStreaksAccess && streaksFTUXCompleted) {
    return { status: "NO_TAG", showFTUX: false, type: "DIRECT_ACCESS" };
  }
  if (!hasStreaksAccess) {
    return { status: "NO_TAG", showFTUX: true, type: "EDUCATION_ONLY" };
  }
  return {
    status: streaksFTUXCompleted ? "NO_TAG" : "UPDATE",
    showFTUX: true,
    type: "EDUCATION_WITH_REALISATION",
  };
};
// Get Sourcing access state (keeping current logic)
export const getSourcingAccessState = (milestones) => {
  const hasSourcingAccess = milestones?.is_sourcing_enabled?.achieved;
  // check if sourcing ftux is completed from home page takeover from userFTUX NOT FROM MILESTONE
  const sourcingFTUXCompleted = !isFeatureInFTUXTakeover("SOURCING");
  if (hasSourcingAccess && sourcingFTUXCompleted) {
    return { status: "NO_TAG", showFTUX: false, type: "DIRECT_ACCESS" };
  }

  if (!hasSourcingAccess) {
    return { status: "NO_TAG", showFTUX: true, type: "EDUCATION_ONLY" };
  }

  const realisationSeen = checkFTUXRealisationSeen("SOURCING", milestones);
  return {
    status: realisationSeen ? "NO_TAG" : "UPDATE",
    showFTUX: true,
    type: "EDUCATION_WITH_REALISATION",
  };
};

// Get Brand Sourcing access state
export const getBrandSourcingAccessState = (milestones, creatorData) => {
  if (creatorData?.creator_account_type === CREATOR_ACCOUNT_TYPE.AFFILIATE) {
    return { status: "LOCKED", showFTUX: true, type: "EDUCATION_ONLY" };
  }

  return {
    status: "NO_TAG",
    showFTUX: true,
    type: "EDUCATION_ONLY",
  };
};

// Main FTUX Feature Access Logic (as per requirements)
export const getFeatureAccessState = (
  feature,
  userAccountType,
  milestones,
  creatorData
) => {
  switch (feature) {
    case "ENGAGE":
      if (userAccountType === "TELEGRAM") {
        return { status: "LOCKED", showFTUX: true, type: "EDUCATION_ONLY" };
      }
      if (userAccountType === "NEW_USER_SL") {
        return {
          status: "LOCKED",
          showFTUX: false,
          type: "EDUCATION_ONLY",
        };
      }
      if (userAccountType === "YOUTUBE_ONLY_SL") {
        return { status: "LOCKED", showFTUX: true, type: "EDUCATION_ONLY" };
      }
      if (userAccountType === "INSTAGRAM_OR_FACEBOOK_SL") {
        return {
          status: "NO_TAG",
          showFTUX: true,
          type: "EDUCATION_WITH_CTA",
        };
      }
      if (userAccountType === "INSTAGRAM_AND_FACEBOOK_SL") {
        return {
          status: "NO_TAG",
          showFTUX: false,
          type: "EDUCATION_WITH_REALISATION",
        };
      }
      break;

    case "COLLAB":
      if (userAccountType === "TELEGRAM" || userAccountType === "NEW_USER_SL") {
        const hasCollabEnabled =
          milestones?.BRAND_COLLAB__COLLAB_LIVE?.achieved;
        const collabFTUXCompleted = !isFeatureInFTUXTakeover("BRAND_COLLAB");

        if (hasCollabEnabled) {
          if (collabFTUXCompleted) {
            return {
              status: "NO_TAG",
              showFTUX: true,
              type: "EDUCATION_WITH_REALISATION",
            };
          }
          return { status: "UPDATE", showFTUX: true, type: "EDUCATION_ONLY" };
        }
        return { status: "LOCKED", showFTUX: true, type: "EDUCATION_ONLY" };
      }
      return getCollabAccessState(milestones, creatorData);

    case "GROWTH":
      if (userAccountType === "TELEGRAM" || userAccountType === "NEW_USER_SL") {
        const hasGrowthEnabled =
          milestones?.GROWTH_V2__PRODUCT_GROWTH?.achieved;
        if (hasGrowthEnabled) {
          const growthFTUXCompleted = !isFeatureInFTUXTakeover("GROWTH_V2");
          if (growthFTUXCompleted) {
            return {
              status: "NO_TAG",
              showFTUX: false,
              type: "EDUCATION_WITH_REALISATION",
            };
          }
          return { status: "UPDATE", showFTUX: true, type: "EDUCATION_ONLY" };
        }
        return { status: "LOCKED", showFTUX: true, type: "EDUCATION_ONLY" };
      }
      return getGrowthAccessState(milestones);

    case "REWARDS":
      if (userAccountType === "TELEGRAM" || userAccountType === "NEW_USER_SL") {
        const hasRewardsAccess = milestones?.REWARDS__REWARDS_LIVE?.achieved;
        const rewardsFTUXCompleted = !isFeatureInFTUXTakeover("REWARDS");
        if (hasRewardsAccess) {
          if (rewardsFTUXCompleted) {
            return {
              status: "NO_TAG",
              showFTUX: false,
              type: "DIRECT_ACCESS",
            };
          }
          return { status: "UPDATE", showFTUX: true, type: "EDUCATION_ONLY" };
        }
        return { status: "LOCKED", showFTUX: true, type: "EDUCATION_ONLY" };
      }
      return getRewardsAccessState(milestones);

    case "STREAKS":
      if (userAccountType === "TELEGRAM" || userAccountType === "NEW_USER_SL") {
        return getStreaksAccessState(milestones);
      }
      return getStreaksAccessState(milestones);

    case "SOURCING":
      if (userAccountType === "TELEGRAM" || userAccountType === "NEW_USER_SL") {
        const hasSourcingAccess = milestones?.is_sourcing_enabled?.achieved;
        const sourcingFTUXCompleted = !isFeatureInFTUXTakeover("SOURCING");
        if (hasSourcingAccess) {
          return {
            status: sourcingFTUXCompleted ? "NO_TAG" : "UPDATE",
            showFTUX: false,
            type: "EDUCATION_ONLY",
          };
        }
        return { status: "LOCKED", showFTUX: true, type: "EDUCATION_ONLY" };
      }
      return getSourcingAccessState(milestones);

    case "BRAND_SOURCING":
      if (userAccountType === "NEW_USER_SL") {
        return {
          status: "LOCKED",
          showFTUX: false,
          type: "EDUCATION_ONLY",
        };
      }
      return getBrandSourcingAccessState(milestones, creatorData);
    case "SELECT_BY_WISHLINK":
      const hasSelectByWishlinkEnabled =
        milestones?.SELECT_BY_WISHLINK__SELECT_BY_WISHLINK_LIVE?.achieved;
      return {
        status: hasSelectByWishlinkEnabled ? "NEW" : "LOCKED",
        showFTUX: false,
        type: "NONE",
      };
    case "CROSSPOSTING":
      // Crossposting is available for users with CROSS_POSTING_V2 feature flag enabled
      const isCrossPostingV2Enabled =
        milestones?.CROSS_POSTING_V2__ENABLED?.achieved;

      // If feature flag is not enabled, hide the feature completely
      if (!isCrossPostingV2Enabled) {
        return {
          status: "HIDDEN",
          showFTUX: false,
          type: "NONE",
        };
      }

      // Always show NEW tag when feature is enabled
      return {
        status: "NEW",
        showFTUX: false,
        type: "NONE",
      };
    case "WHITELISTING": {
      // Lock is social login; FTUX-seen is checked in whitelistingrouter.tsx.
      const hasSocialLogin =
        milestones?.is_social_login_active?.achieved || false;
      return hasSocialLogin
        ? { status: "NO_TAG", showFTUX: true, type: "NONE" }
        : { status: "LOCKED", showFTUX: true, type: "NONE" };
    }
    default:
      return { status: "NO_TAG", showFTUX: false, type: "NONE" };
  }
};

// Home Page Takeover Logic (LIFO principle)
export const getHomeTakeoverRealisation = (featureName) => {
  const features = [
    "BRAND_COLLAB",
    "GROWTH_V2",
    "REWARDS",
    "SOURCING",
    "STREAKS",
  ];
  const realisationPage = features.find((feature) => feature === featureName);
  let component = null;
  if (realisationPage) {
    if (realisationPage === "BRAND_COLLAB") {
      component = "CollabFTUXRealisationPage";
    } else if (realisationPage === "GROWTH_V2") {
      component = "GrowthFTUXRealisationPage";
    } else if (realisationPage === "REWARDS") {
      component = "RewardsFTUXRealisationPage";
    } else if (realisationPage === "SOURCING") {
      component = "SourcingFTUXRealisationPage";
    } else if (realisationPage === "STREAKS") {
      component = "StreaksFTUXRealisationPage";
    }
    return {
      component: component,
      data: { featureName },
    };
  }
  return null;
};

/**
 * Build complete UserDataType including FTUX data from creator config
 * @param {Object} creatorConfigData - Data from useGetCreatorConfigQuery
 * @returns {Object} Complete UserDataType with FTUX data
 */
export const buildUserDataWithFTUX = (creatorConfigData) => {
  if (!creatorConfigData) {
    return null;
  }

  // Get FTUX data from store
  // const state = store.getState();
  // Helper functions for FTUX status
  const hasFeatureInTakeover = (feature) => {
    return isFeatureInFTUXTakeover(feature);
  };

  // Build FTUX status object
  const ftuxStatus = {
    BRAND_COLLAB: hasFeatureInTakeover("BRAND_COLLAB"),
    GROWTH_V2: hasFeatureInTakeover("GROWTH_V2"),
    REWARDS: hasFeatureInTakeover("REWARDS"),
    SOURCING: hasFeatureInTakeover("SOURCING"),
    ENGAGE: hasFeatureInTakeover("ENGAGE_V2"),
    COLLAB_CHAT: hasFeatureInTakeover("COLLAB_CHAT"),
    STREAKS: hasFeatureInTakeover("STREAKS"),
  };

  const authorizationsWithFTUX = {
    ...creatorConfigData.authorizations,
    // Add FTUX authorizations to the main authorizations object
    FTUX_BRAND_COLLAB: {
      achieved: !ftuxStatus.BRAND_COLLAB,
    },
    FTUX_GROWTH_V2: {
      achieved: !ftuxStatus.GROWTH_V2,
    },
    FTUX_REWARDS: {
      achieved: !ftuxStatus.REWARDS,
    },
    FTUX_SOURCING: {
      achieved: !ftuxStatus.SOURCING,
    },
    FTUX_ENGAGE: {
      achieved: !ftuxStatus.ENGAGE,
    },
    FTUX_COLLAB_CHAT: {
      achieved: !ftuxStatus.COLLAB_CHAT,
    },
    FTUX_STREAKS: {
      achieved: !ftuxStatus.STREAKS,
    },
  };
  return {
    ...creatorConfigData,
    authorizations: authorizationsWithFTUX,
  };
};
